import { useState } from "react";
import { getProductCosts } from "../services";
import { featureAccess } from "../permissions/access";
import { useUserRole } from "../utils/hooks/useUserRole";
import { ProductCost, ProductCostList } from "../types/product-cost.type";
import Grid, { GridBaseProps } from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import FilterField from "../components/Product/shared/FilterField";
import { useSearchablePagedRows } from "../components/Product/shared/useSearchablePagedRows";
import ProductCostTable from "../components/Product/Cost/Table";
import ProductCostDialog from "../components/Product/Cost/ProductCostDialog";

const EMPTY_PRODUCT_COSTS: ProductCostList = { rows: [], count: 0 };

const SEARCH_SIZE: GridBaseProps["size"] = { xs: 12, lg: 6 };

// Declared outside the component so the paged list does not refetch on every render.
const fetchProductCosts = async (
  page: number,
  limit: number,
  search?: string,
): Promise<ProductCostList> => {
  const { data: body } = await getProductCosts(page, limit, search);
  return body.data ?? EMPTY_PRODUCT_COSTS;
};

export default function ProductCostPage() {
  const role = useUserRole();
  // Head-office accounting reads costs here; only the catalogue writers edit them.
  const canEditCost = featureAccess.productWrite(role);
  const [editingCost, setEditingCost] = useState<ProductCost | null>(null);

  const {
    search,
    handleChangeSearch,
    page,
    setPage,
    limit,
    setLimit,
    rows: productCosts,
    totalCount,
    loading,
    reload,
  } = useSearchablePagedRows({ open: true, fetchRows: fetchProductCosts });

  const handleCloseEdit = () => {
    setEditingCost(null);
  };

  return (
    <Stack sx={{ py: 1 }}>
      <Stack gap={0.5} mb={1}>
        <Typography variant="h6">ต้นทุนสินค้า</Typography>
        <Typography variant="body2" color="text.secondary">
          ต้นทุนต่อหน่วยของสินค้าแต่ละรายการ
        </Typography>
      </Stack>
      <Paper sx={{ padding: "24px 16px 8px", mb: 2 }}>
        <Grid container spacing={2} mb={2}>
          <FilterField label="ค้นหา" size={SEARCH_SIZE}>
            <TextField
              size="small"
              fullWidth
              placeholder="รหัสสินค้า / ชื่อสินค้า"
              value={search}
              onChange={handleChangeSearch}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </FilterField>
        </Grid>
      </Paper>
      <ProductCostTable
        productCosts={productCosts}
        loading={loading}
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
        canEdit={canEditCost}
        onEdit={setEditingCost}
      />
      <ProductCostDialog
        productCost={editingCost}
        onClose={handleCloseEdit}
        onSaved={reload}
      />
    </Stack>
  );
}
